import React, { useState, useEffect } from 'react';
import { Package, AlertTriangle, XCircle, Eye, Edit, Trash2, Search, Filter, X } from 'lucide-react';
import { getComercioId } from '../config/comercio.js';
import ProductoModal from '../components/ProductoModal';

const ProductosAgotados = () => {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('nombre');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filteredProductos, setFilteredProductos] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Cargar productos agotados
  useEffect(() => {
    cargarProductosAgotados();
  }, []);

  const cargarProductosAgotados = async () => {
    try {
      setLoading(true);
      
      // Obtener token de URL o localStorage
      const token = localStorage.getItem('comercio_token') || localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No se encontró token de comercio');
      }
      
      console.log('🔍 [AGOTADOS] Usando token:', token);
      const response = await fetch(`https://www.sinfilas.com.ar/sf3/ventas/api/productos_agotados.php?token=${token}`);
      
      if (!response.ok) {
        throw new Error('Error al cargar productos agotados');
      }
      
      const data = await response.json();
      
      if (data.success) {
        setProductos(data.productos);
        setFilteredProductos(data.productos);
      } else {
        setError(data.message || 'Error al cargar productos');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('Error de conexión al servidor');
    } finally {
      setLoading(false);
    }
  };

  // Función para manejar la visualización de detalles
  const handleShowDetail = (producto) => {
    setSelectedProduct(producto);
    setShowDetailModal(true);
  };

  // Función para manejar la edición
  const handleEdit = (producto) => {
    console.log('🔧 [EDIT] Iniciando edición de producto:', producto);
    setSelectedProduct(producto);
    setShowModal(true);
    console.log('🔧 [EDIT] Modal abierto, selectedProduct:', producto);
  };

  const formatPrice = (price) => {
    if (!price) return '$0,00';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2
    }).format(price);
  };

  const formatStock = (stock) => {
    if (!stock && stock !== 0) return '0';
    return stock.toString();
  };

  const getStockStatus = (stock, stockMin) => {
    if (stock <= 0) {
      return { text: 'Sin Stock', color: 'stock-status out', icon: '🔴' };
    } else if (stock <= (stockMin || 10)) {
      return { text: 'Bajo Stock', color: 'stock-status low', icon: '🟡' };
    } else {
      return { text: 'Disponible', color: 'stock-status available', icon: '🟢' };
    }
  };

  // Filtrar productos
  useEffect(() => {
    let filtered = productos.filter(producto =>
      producto.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      producto.codigo_personalizado.includes(searchTerm) ||
      producto.marca.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Ordenar productos
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === 'stock_actual' || sortField === 'stock_minimo') {
        aValue = parseInt(aValue) || 0;
        bValue = parseInt(bValue) || 0;
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredProductos(filtered);
  }, [productos, searchTerm, sortField, sortDirection]);

  // Cambiar ordenamiento
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Eliminar producto
  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este producto agotado?')) {
      try {
        // Obtener token de URL o localStorage
        const token = localStorage.getItem('comercio_token') || localStorage.getItem('token');
        
        if (!token) {
          throw new Error('No se encontró token de comercio');
        }
        
        const response = await fetch(`https://www.sinfilas.com.ar/sf3/ventas/api/productos_comercio_eliminar.php?token=${token}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ id }),
        });

        if (response.ok) {
          // Recargar productos
          cargarProductosAgotados();
        } else {
          alert('Error al eliminar el producto');
        }
      } catch (error) {
        console.error('Error:', error);
        alert('Error de conexión');
      }
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Cargando productos agotados...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <AlertTriangle size={48} />
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100">
      <div className="container mx-auto px-6 py-8">
        {/* Header de la página */}
        <div className="page-header-agotados rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">
                🚫 Productos Agotados
              </h1>
              <p className="text-gray-600 mt-2">
                Productos sin stock que requieren reposición
              </p>
            </div>
            
            {/* Estadísticas rápidas */}
            <div className="stats-horizontal-agotados mt-4 lg:mt-0">
              <div className="stats-compact-agotados stats-red">
                <div className="icon-container">
                  <XCircle size={24} />
                </div>
                <div className="text-content">
                  <p className="text-2xl font-bold">{filteredProductos.length}</p>
                  <p className="text-sm">Productos</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Barra de búsqueda */}
        <div className="search-section mb-6">
          <div className="search-container-agotados">
            <div className="search-wrapper-agotados">
              <div className="search-input-wrapper relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar por nombre, código o marca..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-agotados w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
              <button className="btn btn-primary btn-search-agotados px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700">
                Buscar
              </button>
            </div>
          </div>
          <p className="search-info mt-2 text-sm text-gray-600">
            Mostrando {filteredProductos.length} de {productos.length} productos agotados
          </p>
        </div>

        {/* Vista de escritorio - Tabla */}
        {filteredProductos.length > 0 ? (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="desktop-table-view overflow-x-auto">
              <table className="products-table w-full">
                <thead className="bg-gradient-to-r from-red-600 to-red-700 text-white">
                  <tr>
                    <th className="cursor-pointer" onClick={() => handleSort('imagen_url')}>
                      Imagen
                    </th>
                    <th className="cursor-pointer" onClick={() => handleSort('codigo_personalizado')}>
                      Código
                      {sortField === 'codigo_personalizado' && (
                        <span className={`sort-icon ${sortDirection === 'asc' ? 'active' : 'inactive'}`}>
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th className="cursor-pointer" onClick={() => handleSort('nombre')}>
                      Nombre
                      {sortField === 'nombre' && (
                        <span className={`sort-icon ${sortDirection === 'asc' ? 'active' : 'inactive'}`}>
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th className="cursor-pointer" onClick={() => handleSort('marca')}>
                      Marca
                      {sortField === 'marca' && (
                        <span className={`sort-icon ${sortDirection === 'asc' ? 'active' : 'inactive'}`}>
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th className="cursor-pointer" onClick={() => handleSort('categoria')}>
                      Categoría
                      {sortField === 'categoria' && (
                        <span className={`sort-icon ${sortDirection === 'asc' ? 'active' : 'inactive'}`}>
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th className="cursor-pointer" onClick={() => handleSort('stock_actual')}>
                      Stock Actual
                      {sortField === 'stock_actual' && (
                        <span className={`sort-icon ${sortDirection === 'asc' ? 'active' : 'inactive'}`}>
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th className="cursor-pointer" onClick={() => handleSort('stock_minimo')}>
                      Stock Mínimo
                      {sortField === 'stock_minimo' && (
                        <span className={`sort-icon ${sortDirection === 'asc' ? 'active' : 'inactive'}`}>
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th className="cursor-pointer" onClick={() => handleSort('precio_venta')}>
                      Precio
                      {sortField === 'precio_venta' && (
                        <span className={`sort-icon ${sortDirection === 'asc' ? 'active' : 'inactive'}`}>
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProductos.map((producto) => (
                    <tr key={producto.id} className="stock-agotado hover:bg-gray-50">
                      <td>
                        <div className="product-image-container">
                          {producto.imagen_url ? (
                            <img
                              src={producto.imagen_url}
                              alt={producto.nombre}
                              className="product-image"
                            />
                          ) : (
                            <div className="product-image-placeholder">
                              <Package size={20} />
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="codigo-personalizado">
                          <strong>Código:</strong>
                          <span 
                            className="codigo-valor"
                            data-long={producto.codigo_personalizado.length > 8}
                            data-short={producto.codigo_personalizado.length < 6}
                          >
                            {producto.codigo_personalizado}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="product-name">{producto.nombre}</div>
                        <div className="product-brand">{producto.marca}</div>
                      </td>
                      <td>{producto.marca}</td>
                      <td>{producto.categoria}</td>
                      <td>
                        <span className="stock-status out">
                          {producto.stock_actual}
                        </span>
                      </td>
                      <td>
                        <span className="stock-status available">
                          {producto.stock_minimo}
                        </span>
                      </td>
                      <td>
                        <div className="product-price">
                          ${parseFloat(producto.precio_venta).toFixed(2)}
                        </div>
                      </td>
                      <td>
                        <span className="stock-status out">
                          AGOTADO
                        </span>
                      </td>
                      <td>
                        <div className="product-actions">
                          <button
                            className="action-button view"
                            title="Ver detalles"
                            onClick={() => handleShowDetail(producto)}
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            className="action-button edit"
                            title="Editar producto"
                            onClick={() => handleEdit(producto)}
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            className="action-button delete"
                            title="Eliminar producto"
                            onClick={() => handleDelete(producto.id)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Vista móvil - Tarjetas (igual que Productos.jsx) */}
            <div className="mobile-cards-view">
              <div className="space-y-3 p-4">
                {filteredProductos.map((producto) => (
                  <div key={producto.id} className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
                    {/* Header de la tarjeta */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        {/* Imagen del producto */}
                        <div className="product-image-container-mobile">
                          {producto.imagen_url && 
                           producto.imagen_url.trim() !== '' && 
                           !producto.imagen_url.startsWith('blob:') ? (
                            <img
                              src={producto.imagen_url}
                              alt={producto.nombre}
                              className="product-image-mobile"
                              onError={(e) => {
                                if (e.target && e.target.style) {
                                  e.target.style.display = 'none';
                                }
                                if (e.target && e.target.nextSibling && e.target.nextSibling.style) {
                                  e.target.nextSibling.style.display = 'flex';
                                }
                              }}
                            />
                          ) : (
                            <div className="product-image-placeholder-mobile">
                              <Package className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                        </div>
                        
                        {/* Información del producto */}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold text-gray-900 truncate">
                            {producto.nombre}
                          </h3>
                          <p className="text-xs text-gray-500 truncate">
                            {producto.marca || 'Sin marca'}
                          </p>
                          <p className="text-xs text-gray-400 font-mono mt-1">
                            Código: {producto.codigo_personalizado || 'N/A'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Estado del stock */}
                      <div className="flex-shrink-0">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Agotado
                        </span>
                      </div>
                    </div>

                    {/* Información adicional */}
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <p className="text-xs text-gray-500">Categoría</p>
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {producto.categoria || 'Sin categoría'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Precio</p>
                        <p className="text-sm font-medium text-gray-900 truncate">
                          ${producto.precio_venta || '0.00'}
                        </p>
                      </div>
                    </div>

                    {/* Botones de acción */}
                    <div className="flex justify-center space-x-2">
                      <button
                        onClick={() => handleShowDetail(producto)}
                        className="btn-action-mobile"
                        title="Ver detalles"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(producto)}
                        className="btn-action-mobile"
                        title="Editar producto"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(producto.id)}
                        className="btn-action-mobile btn-danger"
                        title="Eliminar producto"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <Package size={64} className="empty-icon" />
            <h3>No hay productos agotados</h3>
            <p>¡Excelente! Todos los productos tienen stock disponible.</p>
          </div>
        )}
      </div>

      {/* Popup de detalles del producto - Idéntico a Productos */}
      {showDetailModal && selectedProduct && (
        <div className="popup-overlay">
          <div className="product-detail-popup">
            {/* Header del popup */}
            <div className="popup-header">
              <h3>📋 {selectedProduct.nombre}</h3>
              <p>{selectedProduct.marca || 'Sin marca'}</p>
              
              {/* Botón de cerrar */}
              <button
                className="popup-close-btn"
                onClick={() => setShowDetailModal(false)}
                title="Cerrar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Contenido del popup */}
            <div className="popup-content">
              <div className="popup-grid">
                {/* Columna izquierda - Imagen y estado */}
                <div className="flex flex-col items-center">
                  {/* Imagen del producto */}
                  {selectedProduct.imagen_url ? (
                    <img 
                      src={selectedProduct.imagen_url} 
                      alt={selectedProduct.nombre}
                      className="popup-product-image"
                    />
                  ) : (
                    <div className="popup-product-image bg-gray-200 flex items-center justify-center">
                      <Package className="w-16 h-16 text-gray-400" />
                    </div>
                  )}

                  {/* Estado del stock */}
                  <div className="mt-4 text-center">
                    <div className={`stock-status-badge ${getStockStatus(selectedProduct.stock_actual, selectedProduct.stock_minimo).color}`}>
                      {getStockStatus(selectedProduct.stock_actual, selectedProduct.stock_minimo).icon}
                      {getStockStatus(selectedProduct.stock_actual, selectedProduct.stock_minimo).text}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Stock: Actual: {formatStock(selectedProduct.stock_actual)}
                    </p>
                  </div>
                </div>

                {/* Columna derecha - Información detallada */}
                <div className="popup-details">
                  {/* Información básica */}
                  <div className="detail-section">
                    <h4>Información Básica</h4>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span className="label">Código:</span>
                        <span className="value">{selectedProduct.codigo_personalizado}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Categoría:</span>
                        <span className="value">{selectedProduct.categoria || 'General'}</span>
                      </div>
                      {selectedProduct.descripcion && (
                        <div className="detail-item full-width">
                          <span className="label">Descripción:</span>
                          <span className="value">{selectedProduct.descripcion}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Información de stock */}
                  <div className="detail-section">
                    <h4>Stock</h4>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span className="label">Actual:</span>
                        <span className="value">{formatStock(selectedProduct.stock_actual)}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Mínimo:</span>
                        <span className="value">{formatStock(selectedProduct.stock_minimo)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Información de precios */}
                  <div className="detail-section">
                    <h4>Precios</h4>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span className="label">Venta:</span>
                        <span className="value price">{formatPrice(selectedProduct.precio_venta)}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Compra:</span>
                        <span className="value price">{formatPrice(selectedProduct.precio_compra)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Botones de acción */}
              <div className="actions-container">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="btn-secondary"
                  title="Cerrar popup"
                >
                  <X className="w-4 h-4" />
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para editar producto - Idéntico a Productos */}
      {showModal && (
        <ProductoModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setSelectedProduct(null);
            // Refrescar la lista de productos al cerrar el modal
            cargarProductosAgotados();
          }}
          onProductAdded={() => {
            // Recargar productos después de editar
            cargarProductosAgotados();
            setShowModal(false);
          }}
          productoExistente={selectedProduct}
        />
      )}
    </div>
  );
};

export default ProductosAgotados;
